import { supabase } from '../lib/supabaseClient';
import type { Item, ItemPayload } from 'types/erp';

export type FilterOp = 'eq' | 'neq' | 'ilike' | 'gte' | 'lte' | 'in';

export type Filter = {
  field: string | string[];
  op: FilterOp;
  value: any;
};

export type Sort = {
  field: string;
  direction?: 'asc' | 'desc';
  nullsFirst?: boolean;
};

export type ListParams = {
  filters?: Filter[];
  sort?: Sort | Sort[];
  page?: number;
  pageSize?: number;
  select?: string;
};

const toLikeValue = (value: string) => `%${value}%`;

const createNormalizedError = (message: string, sourceError: any) => {
  const nextError = new Error(message) as Error & {
    code?: string;
    details?: string;
    hint?: string;
    originalMessage?: string;
  };

  nextError.code = sourceError?.code;
  nextError.details = sourceError?.details;
  nextError.hint = sourceError?.hint;
  nextError.originalMessage = sourceError?.message;

  return nextError;
};

const normalizeSupabaseError = (error: any, table: string) => {
  if (!error) return null;

  const message = String(error?.message || '').toLowerCase();

  if (
    error?.code === '42501' ||
    message.includes('row-level security') ||
    message.includes('permission denied') ||
    message.includes('violates row-level security policy')
  ) {
    return createNormalizedError(
      `${table} verisine erisim yetkiniz yok veya ERP oturumunuz gecersiz. Cikis yapip tekrar giris deneyin.`,
      error
    );
  }

  if (
    error?.code === 'PGRST301' ||
    error?.code === 'PGRST302' ||
    message.includes('jwt') ||
    message.includes('session')
  ) {
    return createNormalizedError('ERP oturumu dogrulanamadi. Cikis yapip tekrar giris yapin.', error);
  }

  if (
    error?.code === 'PGRST000' ||
    error?.code === 'PGRST202' ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('fetch')
  ) {
    return createNormalizedError(
      'Uygulama sunucusuna veya Supabase servisine ulasilamiyor. Farkli cihazdan giriyorsan IP, port ve firewall ayarlarini kontrol et.',
      error
    );
  }

  return error;
};

const applySingleFilter = (query: any, field: string, op: FilterOp, value: any) => {
  if (value === undefined) return query;

  switch (op) {
    case 'eq':
      return value === null ? query.is(field, null) : query.eq(field, value);
    case 'neq':
      return value === null ? query.not(field, 'is', null) : query.neq(field, value);
    case 'ilike':
      return value === null || value === '' ? query : query.ilike(field, toLikeValue(String(value)));
    case 'gte':
      return query.gte(field, value);
    case 'lte':
      return query.lte(field, value);
    case 'in':
      return Array.isArray(value) ? query.in(field, value) : query.in(field, [value]);
    default:
      return query;
  }
};

const buildOrCondition = (field: string, op: FilterOp, value: any) => {
  switch (op) {
    case 'eq':
      return `${field}.eq.${value}`;
    case 'neq':
      return `${field}.neq.${value}`;
    case 'ilike':
      return `${field}.ilike.${toLikeValue(String(value))}`;
    case 'gte':
      return `${field}.gte.${value}`;
    case 'lte':
      return `${field}.lte.${value}`;
    default:
      return `${field}.eq.${value}`;
  }
};

const applyFilters = (query: any, filters: Filter[] = []) => {
  let nextQuery = query;

  filters.forEach((filter) => {
    if (filter.value === undefined || filter.value === '') return;

    if (Array.isArray(filter.field)) {
      const orConditions = filter.field.map((field) => buildOrCondition(field, filter.op, filter.value));
      nextQuery = nextQuery.or(orConditions.join(','));
    } else {
      nextQuery = applySingleFilter(nextQuery, filter.field, filter.op, filter.value);
    }
  });

  return nextQuery;
};

export const getList = async (table: string, params: ListParams = {}) => {
  const { filters = [], sort, page, pageSize, select = '*' } = params;

  let query = supabase.from(table).select(select, { count: 'exact' });
  query = applyFilters(query, filters);

  const sortList = Array.isArray(sort) ? sort : sort ? [sort] : [];
  sortList.forEach((sortItem) => {
    if (sortItem?.field) {
      query = query.order(sortItem.field, { ascending: sortItem.direction !== 'desc', nullsFirst: sortItem.nullsFirst });
    }
  });

  if (page && pageSize) {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);
  }

  const { data, error, count } = await query;
  if (error) throw normalizeSupabaseError(error, table);
  return { data: data ?? [], count: count ?? 0 };
};

export const getById = async (table: string, id: string | number, select = '*') => {
  const { data, error } = await supabase.from(table).select(select).eq('id', id).single();
  if (error) throw normalizeSupabaseError(error, table);
  return data;
};

export const create = async (table: string, payload: Record<string, any>, select = '*') => {
  const { data, error } = await supabase.from(table).insert([payload]).select(select).single();
  if (error) throw normalizeSupabaseError(error, table);
  return data;
};

export const createMany = async (table: string, payloads: Record<string, any>[], select = '*') => {
  if (!payloads || payloads.length === 0) return [];
  const { data, error } = await supabase.from(table).insert(payloads).select(select);
  if (error) throw normalizeSupabaseError(error, table);
  return data ?? [];
};

export const update = async (table: string, id: string | number, payload: Record<string, any>, select = '*') => {
  const { data, error } = await supabase.from(table).update(payload).eq('id', id).select(select).single();
  if (error) throw normalizeSupabaseError(error, table);
  return data;
};

export const remove = async (table: string, id: string | number) => {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw normalizeSupabaseError(error, table);
};

export const removeMany = async (table: string, ids: Array<string | number>) => {
  if (!ids || ids.length === 0) return;
  const uniqueIds = [...new Set(ids)];
  const { data, error } = await supabase.from(table).delete().in('id', uniqueIds).select('id');
  if (error) throw normalizeSupabaseError(error, table);
  if (data?.length !== uniqueIds.length) {
    throw new Error(
      'Bazı kayıtlar silinemedi. Kayıtlar daha önce silinmiş olabilir veya silme yetkiniz olmayabilir. Listeyi yenileyip tekrar deneyin.'
    );
  }
};

export type { Item, ItemPayload };

export const createItem = async (payload: ItemPayload) => {
  return create('items', payload);
};

export const updateItem = async (id: string, payload: Partial<ItemPayload>) => {
  return update('items', id, payload);
};

export const getItemBalances = async (itemId: string | number) => {
  const { data, error } = await supabase.from('inventory_balances').select('*').eq('item_id', itemId);
  if (error) throw normalizeSupabaseError(error, 'inventory_balances');
  return data ?? [];
};

export const getItemMoves = async (itemId: string | number) => {
  const { data, error } = await supabase
    .from('inventory_moves')
    .select(`
      *,
      items (id, item_name, item_code, uom),
      from_warehouse:warehouses!inventory_moves_from_warehouse_id_fkey (id, warehouse_name),
      to_warehouse:warehouses!inventory_moves_to_warehouse_id_fkey (id, warehouse_name),
      from_location:locations!inventory_moves_from_location_id_fkey (id, location_code),
      to_location:locations!inventory_moves_to_location_id_fkey (id, location_code)
    `)
    .eq('item_id', itemId)
    .order('move_time', { ascending: false });
  if (error) throw normalizeSupabaseError(error, 'inventory_moves');
  return data ?? [];
};
